import React from 'react'
import { Icon, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shopflo/ui'
import ColorPickerV2 from 'src/components/common/ColorPickerV2'
import { InputSlider } from 'src/components/common/slider/InputSlider'
import { useCartContext } from 'src/lib/contexts/CartContext'
import { BORDER_SIDES_OPTIONS, BorderSides } from '../config'
import { PaintBrush } from '@phosphor-icons/react'
import { useRouter } from 'next/router'
import { CouponVisibiltyExtentType } from 'src/lib/types/merchantAttributes'
import { SLIDER_CONSTANTS } from 'src/lib/utils/constants'
import FloCheckbox from 'src/components/common/form-elements/FloCheckbox'

const DISPLAY_OPTIONS: { value: CouponVisibiltyExtentType; label: string; description: string }[] = [
    { value: 'cards', label: 'Highlighted', description: 'Coupon code is highlighted in the cart' },
    { value: 'codeEntry', label: 'Input field', description: 'User enters a coupon code manually' }
]

const COLOR_SECTIONS_MAP: Record<
    string,
    {
        title: string
        section: 'callout' | 'footer' | 'input' | 'button' | 'totalSavings'
        colors: { label: string; key: string }[]
    }[]
> = {
    cards: [
        {
            title: 'Callout',
            section: 'callout',
            colors: [
                { label: 'Background', key: 'backgroundColor' },
                { label: 'Primary', key: 'primaryColor' },
                { label: 'Info', key: 'infoColor' },
                { label: 'Border', key: 'borderColor' }
            ]
        },
        {
            title: 'Footer',
            section: 'footer',
            colors: [
                { label: 'Background', key: 'backgroundColor' },
                { label: 'Primary', key: 'primaryColor' },
                { label: 'Info', key: 'infoColor' }
            ]
        }
    ],
    codeEntry: [
        {
            title: 'Input',
            section: 'input',
            colors: [
                { label: 'Background', key: 'backgroundColor' },
                { label: 'Border', key: 'borderColor' },
                { label: 'Primary', key: 'primaryColor' }
            ]
        },
        {
            title: 'Button',
            section: 'button',
            colors: [
                { label: 'Background', key: 'backgroundColor' },
                { label: 'Text', key: 'textColor' }
            ]
        }
    ]
}

export const DiscountControl = () => {
    const router = useRouter()
    const {
        getters: { cartUIData },
        actions: { setCartUIConfigData, setCartCouponVisibilityConfig }
    } = useCartContext()

    const cartVisibilityConfig = cartUIData.couponVisibilityConfig.find(config => {
        return config.type === 'CART'
    })

    const selectedDisplayType = cartVisibilityConfig?.visibilityExtent ?? 'cards'

    const handleConfigChange = (updates: Record<string, any>) => {
        setCartUIConfigData(updates, 'DISCOUNT')
    }

    const handleNestedConfigChange = (
        section: 'callout' | 'footer' | 'input' | 'button' | 'totalSavings',
        updates: Record<string, any>
    ) => {
        setCartUIConfigData(
            {
                [section]: {
                    ...cartUIData.discountConfig[section],
                    ...updates
                }
            },
            'DISCOUNT'
        )
    }

    const renderColorSection = (
        title: string,
        section: 'callout' | 'footer' | 'input' | 'button' | 'totalSavings',
        colors: { label: string; key: string }[]
    ) => (
        <div className='flex flex-col gap-3 mb-2'>
            <p className='text-text-xs-medium text-zinc-500'>{title}</p>
            <div className='flex flex-col gap-2'>
                {Array.from({ length: Math.ceil(colors.length / 2) }, (_, rowIndex) => (
                    <div key={rowIndex} className='grid grid-cols-2 gap-2'>
                        {colors.slice(rowIndex * 2, rowIndex * 2 + 2).map(({ label, key }) => {
                            return (
                                <ColorPickerV2
                                    key={key}
                                    label={label}
                                    color={
                                        cartUIData.discountConfig[section][key as keyof typeof cartUIData.discountConfig[typeof section]]
                                    }
                                    onChange={color => handleNestedConfigChange(section, { [key]: color })}
                                />
                            )
                        })}
                    </div>
                ))}
            </div>
        </div>
    )

    return (
        <div className='flex flex-col gap-3'>
            {/* Display coupon as */}
            <div className='flex items-center gap-2'>
                <span className='text-text-sm-medium text-zinc-600 flex-1'>Display coupon as</span>
                <Select
                    value={selectedDisplayType}
                    onValueChange={(value: CouponVisibiltyExtentType) => {
                        const updatedConfig = cartUIData.couponVisibilityConfig.map(config =>
                            config.type === 'CART' ? { ...config, visibilityExtent: value } : config
                        )
                        setCartCouponVisibilityConfig(updatedConfig)
                    }}
                >
                    <SelectTrigger className='w-[178px] h-8 bg-white'>
                        <div className='text-text-sm-regular'>
                            {DISPLAY_OPTIONS.find(option => option.value === selectedDisplayType)?.label}
                        </div>
                    </SelectTrigger>
                    <SelectContent>
                        {DISPLAY_OPTIONS.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                                <div>
                                    <p className='text-text-sm-regular text-zinc-600'>{option.label}</p>
                                    <p className='text-text-xs-regular text-zinc-500'>{option.description}</p>
                                </div>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className='flex items-center justify-between gap-3'>
                <div className='flex flex-col justify-center flex-1'>
                    <p className='text-text-sm-medium text-zinc-600'>Show coupon savings</p>
                </div>
                <FloCheckbox
                    type='SWITCH'
                    value={!Boolean(cartVisibilityConfig?.hideSavings)}
                    setValue={(value: boolean) => {
                        const updatedConfig = cartUIData.couponVisibilityConfig.map(config =>
                            config.type === 'CART' ? { ...config, hideSavings: !value } : config
                        )
                        setCartCouponVisibilityConfig(updatedConfig)
                    }}
                />
            </div>

            <div className='border-t border-zinc-950/6' />

            {COLOR_SECTIONS_MAP[selectedDisplayType]?.map(({ title, section, colors }) => (
                <React.Fragment key={section}>{renderColorSection(title, section, colors)}</React.Fragment>
            ))}

            {renderColorSection('Total Savings', 'totalSavings', [
                { label: 'Primary', key: 'primaryColor' },
                { label: 'Info', key: 'infoColor' },
                { label: 'Background', key: 'backgroundColor' }
            ])}

            <div className='border-t border-zinc-950/6' />

            <InputSlider
                label='Card radius'
                value={cartUIData.discountConfig.cardRadius}
                onChange={value => handleConfigChange({ cardRadius: value })}
                min={0}
                max={SLIDER_CONSTANTS.MAX_RADIUS}
                step={1}
                showDots={true}
                dotCount={5}
                unit='px'
            />

            {/* Card Border Width */}
            <div className='flex flex-col gap-1'>
                <InputSlider
                    label='Card border'
                    value={cartUIData.discountConfig.cardBorderWidth}
                    onChange={value => handleConfigChange({ cardBorderWidth: value })}
                    min={0}
                    max={SLIDER_CONSTANTS.MAX_BORDER_WIDTH}
                    step={1}
                    showDots={true}
                    dotCount={5}
                    unit='px'
                />

                {/* Card Border Sides Selector */}
                <div className='flex items-center justify-end gap-2 mt-1'>
                    <Select
                        value={cartUIData.discountConfig.cardBorderSides}
                        onValueChange={(value: BorderSides) => handleConfigChange({ cardBorderSides: value })}
                    >
                        <SelectTrigger className='w-[178px] h-8 bg-white'>
                            <SelectValue placeholder='Select sides' />
                        </SelectTrigger>
                        <SelectContent>
                            {BORDER_SIDES_OPTIONS.map(option => (
                                <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Divider */}
            <div className='border-t border-zinc-950/6' />

            <button className='text-text-xs-semibold flex items-center gap-0.5' onClick={() => router.push('/cart/coupons')}>
                <Icon IconComponent={PaintBrush} size='sm' iconProps={{ className: 'text-sky-600', weight: 'duotone' }} />
                <span className='text-sky-600'>Customize Coupon page?</span>
            </button>
        </div>
    )
}
